import { useState } from 'react';
import { QuickstartCard } from './QuickstartCard';
import { QuickstartTheme, getThemePrimaryTextColor } from '../types/theme';
import { EnvironmentInfo, getDefaultBrowserForPlatform, getCurrentPageUrl } from '../utils/environmentDetection';
import { Button } from '@playcastdotio/Shadcn';
import { sendAnalytics } from '../../../telemetry/client';
import { getPersistentPlayerId } from '../../../api';
import { getCampaignIdFromUrl } from '../../../config/campaignUrls';

/**
 * Get persistentId - try URL first, then fall back to traditional method
 * Checks both 'persid' and 'id' query parameters in URL
 * Falls back to getPersistentPlayerId() if not found in URL
 */
const getPersistentId = (): string => {
  const searchParams = new URLSearchParams(window.location.search);
  const persid = searchParams.get('persid');

  // Return persid if it exists and is valid
  if (persid && persid.startsWith('player_')) {
    return persid;
  }

  // Fall back to traditional method (creates one if doesn't exist)
  return getPersistentPlayerId();
};

/**
 * Get campaignId from current URL path
 * Extracts the slug from /q/{slug} and converts to campaignId
 */
const getCampaignIdFromCurrentUrl = (): string | undefined => {
  if (typeof window === 'undefined') return undefined;

  const pathMatch = window.location.pathname.match(/\/q\/([^\/]+)/);
  if (!pathMatch) return undefined;

  const slug = pathMatch[1];
  return getCampaignIdFromUrl(slug) ?? undefined;
};

interface EmbeddedBrowserRedirectProps {
  theme: QuickstartTheme;
  environment: EnvironmentInfo;
  onOpenInBrowserClicked: () => void;
}

export const EmbeddedBrowserRedirect = ({ theme, environment, onOpenInBrowserClicked }: EmbeddedBrowserRedirectProps) => {
  const currentUrl = getCurrentPageUrl();
  const [copyStatus, setCopyStatus] = useState<'idle' | 'success' | 'failed'>('idle');

  const handleCopyClick = async () => {
    // Send analytics event
    sendAnalytics({
      name: 'Embedded Copy URL',
      role: 'player',
      persistentId: getPersistentId(),
      campaignId: getCampaignIdFromCurrentUrl(),
      copiedUrl: currentUrl,
      flow: 'quickstart',
      tier2Filters: ['deaddrop'],
    });

    try {
      await navigator.clipboard.writeText(currentUrl);
      setCopyStatus('success');
      console.log('[EmbeddedBrowserRedirect] Link copied to clipboard');
    } catch (err) {
      setCopyStatus('failed');
      console.warn('[EmbeddedBrowserRedirect] Failed to copy to clipboard:', err);
    }
    onOpenInBrowserClicked();
  };

  return (
    <div
      className='flex min-h-screen items-center justify-center overflow-auto p-4'
      style={{
        backgroundImage: theme.pageBackgroundImage ? `url(${theme.pageBackgroundImage})` : undefined,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      <QuickstartCard theme={theme} showHeader={true} hideBadge={true} hideStudioLogo={true}>
        <div className='flex flex-col items-center space-y-6 text-center'>
          {/* Main heading */}
          <div
            style={{
              width: '100%',
              textAlign: 'center',
              justifyContent: 'center',
              display: 'flex',
              flexDirection: 'column',
              color: 'var(--Color-Primary-Regular, #FBBF24)',
              fontSize: 24,
              fontFamily: 'Inter',
              fontWeight: '700',
              lineHeight: '40px',
              wordWrap: 'break-word',
            }}
          >
            Open this page in browser
          </div>

          {/* GIF - Full width, breaking out of padding */}
          <div
            style={{
              width: 'calc(100% + 300px)',
              marginLeft: '-32px',
              marginRight: '-32px',
            }}
          >
            <img
              src='/quickstart/phone-ad-3.gif'
              alt='Instructions'
              style={{
                width: '100%',
                height: 'auto',
                display: 'block',
              }}
            />
          </div>

          {/* Divider with "or" in the middle */}
          <div
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
            }}
          >
            <div
              style={{
                flex: 1,
                height: '1px',
                outline: '1px var(--Color-Primary-Light, #FEF3C7) solid',
                outlineOffset: '-0.50px',
              }}
            />
            <div
              style={{
                justifyContent: 'center',
                display: 'flex',
                flexDirection: 'column',
                color: 'var(--Color-Primary-Light, #FEF3C7)',
                fontSize: 20,
                fontFamily: 'Inter',
                fontWeight: '400',
                wordWrap: 'break-word',
              }}
            >
              or
            </div>
            <div
              style={{
                flex: 1,
                height: '1px',
                outline: '1px var(--Color-Primary-Light, #FEF3C7) solid',
                outlineOffset: '-0.50px',
              }}
            />
          </div>

          {/* Clickable copy link */}
          <div
            onClick={handleCopyClick}
            style={{
              width: '100%',
              textAlign: 'center',
              justifyContent: 'center',
              display: 'flex',
              flexDirection: 'column',
              color: '#28A2FF',
              fontSize: 16,
              fontFamily: 'Inter',
              fontWeight: '400',
              textDecoration: 'underline',
              wordWrap: 'break-word',
              cursor: 'pointer',
            }}
          >
            Tap to copy URL, then paste it into your browser
          </div>

          {/* Optional: Show copy status feedback */}
          {copyStatus === 'success' && <p className='text-xs text-green-400'>✓ Link copied to clipboard!</p>}
          {copyStatus === 'failed' && <p className='text-xs text-red-400'>Failed to copy. Please copy manually: {currentUrl}</p>}
        </div>
      </QuickstartCard>
    </div>
  );
};

